import { useState, useCallback, useMemo } from 'react';
import { BlueprintLocation } from '../../types/protocols';
import { ParkingArea, ParkingAreaInput, ParkingSpot, ParkingSpotInput } from '../../types/parking';
import { LevelBlueprintGuard, parkingApi, StaleBlueprintError } from '../../api/parkingApi';
import { RoomCreateInput, roomsApi } from '../../api/roomsApi';
import { ParkingBlueprintSource } from '../../types/Enums';
import { useBuilding } from '../../Helpers/BuildingContext';

interface UseParkingProps {
    buildingKey: string | null | undefined;
}

interface UseParkingReturn {
    areas: { [areaKey: string]: ParkingArea };
    sortedAreas: ParkingArea[];
    loading: boolean;
    error: string | null;
    submitting: boolean;
    createArea: (input: ParkingAreaInput) => Promise<ParkingArea>;
    updateArea: (areaKey: string, input: ParkingAreaInput) => Promise<void>;
    deleteArea: (areaKey: string) => Promise<void>;
    deleteCurrentBlueprint: (areaKey: string, blueprintKey: string) => Promise<void>;
    /** Drawing action: use the level's drawing or stop using it. Refetches the building. */
    setBlueprintSource: (areaKey: string, source: ParkingBlueprintSource) => Promise<void>;
    /** Creates a room on a level from the area form; refetches the building, returns the key. */
    createRoom: (input: RoomCreateInput) => Promise<string>;
    /** After BlueprintUploadModal wrote a new drawing: stamp the area and refetch. */
    stampAreaUpdated: (areaKey: string) => Promise<void>;
    /** Pass `levelGuard` when the location is keyed by a level drawing (level mode). */
    createSpot: (areaKey: string, input: ParkingSpotInput, location?: BlueprintLocation, levelGuard?: LevelBlueprintGuard) => Promise<ParkingSpot>;
    updateSpot: (areaKey: string, spotKey: string, input: ParkingSpotInput) => Promise<void>;
    setSpotLocation: (areaKey: string, spotKey: string, imageKey: string, location: BlueprintLocation | null, levelGuard?: LevelBlueprintGuard) => Promise<void>;
    deleteSpot: (areaKey: string, spotKey: string) => Promise<void>;
    deleteSpotPhoto: (areaKey: string, spotKey: string, photoKey: string) => Promise<void>;
    /**
     * Uploads spot photos: verifies admin, hands the path config to the
     * caller's uploader (usePhotoUpload.uploadPhotos), stamps the spot, refetches.
     */
    uploadSpotPhotos: (
        areaKey: string,
        spotKey: string,
        uploader: (config: { storagePath: (photoKey: string) => string; dbPath: (photoKey: string) => string }) => Promise<unknown>
    ) => Promise<void>;
    refresh: () => Promise<void>;
    clearError: () => void;
}

/**
 * Parking areas of the current building. The data comes from the already
 * loaded BuildingContext (`model.parking`); BuildingContext has no per-subtree
 * patch, so every mutation refetches the building afterwards (plan D11).
 */
export const useParking = ({ buildingKey }: UseParkingProps): UseParkingReturn => {
    const { currentBuilding, loadingBuilding, fetchBuilding } = useBuilding();
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const areas = useMemo(() => {
        if (!currentBuilding || currentBuilding.key !== buildingKey) return {};
        return parkingApi.parseAreas(currentBuilding.model.parking);
    }, [currentBuilding, buildingKey]);

    const sortedAreas = useMemo(() => {
        return Object.values(areas).sort((a, b) =>
            (a.name || '').localeCompare(b.name || '', 'sv', { numeric: true }) || a.key.localeCompare(b.key)
        );
    }, [areas]);

    const refresh = useCallback(async () => {
        if (buildingKey) {
            await fetchBuilding(buildingKey);
        }
    }, [buildingKey, fetchBuilding]);

    const run = useCallback(async <T,>(fallbackMessage: string, action: () => Promise<T>): Promise<T> => {
        if (!buildingKey) {
            const message = 'Ingen byggnad vald';
            setError(message);
            throw new Error(message);
        }
        setSubmitting(true);
        setError(null);
        try {
            const result = await action();
            await refresh();
            return result;
        } catch (err) {
            console.error(fallbackMessage, err);
            setError(err instanceof Error ? err.message : fallbackMessage);
            // Cached level drawing key was outdated: load the current one so the user can place again.
            if (err instanceof StaleBlueprintError) {
                await refresh().catch(() => undefined);
            }
            throw err;
        } finally {
            setSubmitting(false);
        }
    }, [buildingKey, refresh]);

    const createArea = useCallback((input: ParkingAreaInput) =>
        run('Kunde inte skapa parkeringsområdet', () => parkingApi.createArea(buildingKey!, input)),
    [run, buildingKey]);

    const updateArea = useCallback((areaKey: string, input: ParkingAreaInput) =>
        run('Kunde inte uppdatera parkeringsområdet', () => parkingApi.updateArea(buildingKey!, areaKey, input)),
    [run, buildingKey]);

    const deleteArea = useCallback((areaKey: string) =>
        run('Kunde inte ta bort parkeringsområdet', () => parkingApi.deleteArea(buildingKey!, areaKey)),
    [run, buildingKey]);

    const deleteCurrentBlueprint = useCallback((areaKey: string, blueprintKey: string) =>
        run('Kunde inte ta bort ritningen', () => parkingApi.deleteCurrentBlueprint(buildingKey!, areaKey, blueprintKey)),
    [run, buildingKey]);

    const setBlueprintSource = useCallback((areaKey: string, source: ParkingBlueprintSource) =>
        run('Kunde inte ändra ritning', () => parkingApi.setBlueprintSource(buildingKey!, areaKey, source)),
    [run, buildingKey]);

    const createRoom = useCallback((input: RoomCreateInput) =>
        run('Kunde inte skapa rummet', () => roomsApi.createRoom(buildingKey!, input)),
    [run, buildingKey]);

    const stampAreaUpdated = useCallback((areaKey: string) =>
        run('Kunde inte uppdatera parkeringsområdet', () => parkingApi.stampAreaUpdated(buildingKey!, areaKey)),
    [run, buildingKey]);

    const createSpot = useCallback((areaKey: string, input: ParkingSpotInput, location?: BlueprintLocation, levelGuard?: LevelBlueprintGuard) =>
        run('Kunde inte skapa parkeringsplatsen', () => parkingApi.createSpot(buildingKey!, areaKey, input, location, levelGuard)),
    [run, buildingKey]);

    const updateSpot = useCallback((areaKey: string, spotKey: string, input: ParkingSpotInput) =>
        run('Kunde inte uppdatera parkeringsplatsen', () => parkingApi.updateSpot(buildingKey!, areaKey, spotKey, input)),
    [run, buildingKey]);

    const setSpotLocation = useCallback((areaKey: string, spotKey: string, imageKey: string, location: BlueprintLocation | null, levelGuard?: LevelBlueprintGuard) =>
        run('Kunde inte placera parkeringsplatsen', () => parkingApi.setSpotLocation(buildingKey!, areaKey, spotKey, imageKey, location, levelGuard)),
    [run, buildingKey]);

    const deleteSpot = useCallback((areaKey: string, spotKey: string) =>
        run('Kunde inte ta bort parkeringsplatsen', () => parkingApi.deleteSpot(buildingKey!, areaKey, spotKey)),
    [run, buildingKey]);

    const deleteSpotPhoto = useCallback((areaKey: string, spotKey: string, photoKey: string) =>
        run('Kunde inte ta bort fotot', () => parkingApi.deleteSpotPhoto(buildingKey!, areaKey, spotKey, photoKey)),
    [run, buildingKey]);

    const uploadSpotPhotos = useCallback((
        areaKey: string,
        spotKey: string,
        uploader: (config: { storagePath: (photoKey: string) => string; dbPath: (photoKey: string) => string }) => Promise<unknown>
    ) =>
        run('Kunde inte ladda upp foton', async () => {
            const config = await parkingApi.prepareSpotPhotoUpload(buildingKey!, areaKey, spotKey);
            await uploader(config);
            await parkingApi.stampSpotUpdated(buildingKey!, areaKey, spotKey);
        }),
    [run, buildingKey]);

    const clearError = useCallback(() => setError(null), []);

    return {
        areas,
        sortedAreas,
        loading: loadingBuilding,
        error,
        submitting,
        createArea,
        updateArea,
        deleteArea,
        deleteCurrentBlueprint,
        setBlueprintSource,
        createRoom,
        stampAreaUpdated,
        createSpot,
        updateSpot,
        setSpotLocation,
        deleteSpot,
        deleteSpotPhoto,
        uploadSpotPhotos,
        refresh,
        clearError,
    };
};
